


































import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import SingleComment, { EditCommentEmission, DeleteCommentEmission } from './SingleComment.vue'
import Redactor from '@/components/Redactor.vue'
import { Button } from '@alpha/ink'
import { orderBy, isEqual } from 'lodash-es'
import * as db from '@shared/types/database'
import api from '@/api'
import store from '@/store'
import * as ui from '@/types/frontend'

declare global {
  interface Window { taggedUsers: ui.TaggedUser[] }
}

@Component({
  components: {
    SingleComment,
    Button,
    Redactor
  }
})
export default class Comments extends Vue {
  @Prop({ required: true }) comments!: Record<string, db.MongoComment>
  @Prop({ required: true }) users!: ui.User[]
  @Prop({ required: true }) workspaceId!: string
  @Prop({ required: true }) testId!: string

  show: boolean = false
  error: boolean = false
  newComment: string = ''
  submitting: boolean = false
  fixedComments: Record<string, ui.FixedComment> = {}

  get sortedComments () {
    return orderBy(this.fixedComments, ['correctTimestamp'], ['desc'])
  }

  get isDisabled () {
    return this.submitting || this.newComment.trim().length === 0
  }

  get isNoComment () {
    return !Object.keys(this.fixedComments).length
  }

  get authenticatedUser () {
    return store.getters.user.authenticatedUser
  }

  get firstname () {
    return this.authenticatedUser.firstName
  }

  get formattedUsersToTag () {
    const users = this.users
    return users.reduce(function (acc, user) {
      // ALWAYS LEAVE A BLANK SPACE BETWEEN @USERNAME AND THE HTML TAGS
      const replacement = `<a uid="${user.id}"> @${user.userName}</a>`
      acc[user.userName] = { item: user.fullName, replacement: replacement, id: user.id, username: user.userName }
      return acc
    }, {} as Record<string, ui.TaggedUser>)
  }

  @Watch('comments', { immediate: true })
  fixComments (nComm: db.TestPayload['comments'], oComm: db.TestPayload['comments']) {
    if (isEqual(nComm, oComm)) return
    this.fixedComments = Object.keys(this.comments).reduce((acc, key) => {
      const value: ui.FixedComment = Object.assign({}, this.comments[key], {
        correctTimestamp: parseInt(key, 10),
        commentId: key
      })
      acc[key] = value
      return acc
    }, {} as Record<string, ui.FixedComment>)
  }

  toggleNewComment () {
    if (this.submitting) { return }
    this.show = false
    this.newComment = ''
  }

  submitComment () {
    this.submitting = true
    /// reef/src/vendor/redactor/_plugins/handle/handle-alpha.js stores the tagged users on the window

    var commentObj: ui.CreateCommentInput = {
      userid: this.authenticatedUser.uid,
      firstname: this.authenticatedUser.firstName,
      lastname: this.authenticatedUser.lastName,
      email: this.authenticatedUser.email,
      comment: this.newComment.trim(),
      tagged_user_ids: window.taggedUsers ? window.taggedUsers.map(user => user.id) : []
    }

    const { workspaceId, testId } = this
    api.addComment(commentObj, { workspaceId, testId }).then((response) => {
      const { id } = response
      window.taggedUsers = []
      this.$set(this.fixedComments, id, Object.assign(commentObj, {
        correctTimestamp: parseInt(id, 10),
        commentId: id
      }))
    }).catch(() => {
      this.error = true
      setTimeout(() => { this.error = false }, 3000)
    }).finally(() => {
      this.submitting = false
      this.toggleNewComment()
    })
  }

  editComment (eventObject: EditCommentEmission) {
    const editedComment: ui.EditCommentInput = {
      comment_id: eventObject.commentId,
      comment: eventObject.editedComment,
      userid: this.authenticatedUser.uid,
      firstname: this.authenticatedUser.firstName,
      email: this.authenticatedUser.email,
      edited: true
    }
    const { workspaceId, testId } = this
    api.editComment(editedComment, { workspaceId, testId }).then(() => {
      this.fixedComments[eventObject.commentId].comment = eventObject.editedComment
      if (eventObject.editedComment.trim() !== eventObject.originalComment.trim()) {
        this.fixedComments[eventObject.commentId].edited = true
      }
      eventObject.component.finish(true)
    }).catch(() => {
      eventObject.component.finish(false)
    })
  }

  deleteComment (eventObject: DeleteCommentEmission) {
    const deletedComment: ui.DeleteCommentInput = {
      comment_id: eventObject.commentId
    }
    const { workspaceId, testId } = this
    api.deleteComment(deletedComment, { workspaceId, testId }).then(() => {
      eventObject.component.finish(true)
      this.$delete(this.fixedComments, eventObject.commentId)
    }).catch(function () {
      eventObject.component.finish(false)
    })
  }
}
